import React, { useEffect, useState, useCallback } from "react";
import { MapContainer, TileLayer, Marker, Tooltip, GeoJSON } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import ForecastChart from "./ForecastChart";  
import { MapHomeControl, MapInfoControl } from "../../../common/MapControls/MapControls";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

// Distinct colors for compared stations
const COMPARE_COLORS = ["#2563eb", "#e11d48", "#059669", "#d97706", "#7c3aed", "#0891b2"];

export default function StationAnalysisPage() {
    const [stations, setStations] = useState([]);
    const [DiliRivers, setDiliRivers] = useState(null);

    // Multi-station comparison state: array of { station, hydroData, geoData, color }
    const [comparedStations, setComparedStations] = useState([]);

    const stationIcon = L.icon({
        iconUrl: "/DATA/Flood/outlet.png",
        iconSize: [18, 18],
        iconAnchor: [9, 9],
    });

    const activeStationIcon = L.icon({
        iconUrl: "/DATA/Flood/outlet.png",
        iconSize: [24, 24],
        iconAnchor: [12, 12],
    });

    useEffect(() => {
        fetch(`${backendUrl}/flood/hydrological-stations/`)
            .then((res) => res.json())
            .then((data) => Array.isArray(data) && setStations(data))
            .catch((err) => console.error("Stations load error:", err));
    }, []);

    useEffect(() => {
        fetch("/DATA/Flood/dili_river.geojson")
            .then((res) => res.json())
            .then(setDiliRivers)
            .catch((err) => console.error("Boundary load error:", err));
    }, []);

    const fetchStationData = useCallback((station) => {
        const color = COMPARE_COLORS[comparedStations.length % COMPARE_COLORS.length];
        // Add placeholder entry immediately (loading state)
        const entry = { station, hydroData: [], geoData: [], color, loading: true };
        setComparedStations(prev => [...prev, entry]);

        Promise.all([
            fetch(`${backendUrl}flood/hydrological-stations-discharge-data/?station_id=${station.id}`)
                .then(r => r.json()).catch(() => []),
            fetch(`${backendUrl}flood/geoglows_forecast/?river_id=${station.river_id}`)
                .then(r => r.json()).catch(() => []),
        ]).then(([hydro, geo]) => {
            setComparedStations(prev =>
                prev.map(e =>
                    e.station.id === station.id
                        ? { ...e, hydroData: Array.isArray(hydro) ? hydro : [], geoData: Array.isArray(geo) ? geo : [], loading: false }
                        : e
                )
            );
        });
    }, [comparedStations.length]);

    const handleStationClick = (station) => {
        // If already compared, ignore
        if (comparedStations.some(e => e.station.id === station.id)) return;

        if (comparedStations.length === 0) {
            // First click — add as primary
            fetchStationData(station);
        } else {
            // Subsequent clicks — add for comparison (max 4)
            if (comparedStations.length >= 4) return;
            fetchStationData(station);
        }
    };

    const removeStation = (stationId) => {
        setComparedStations(prev => {
            const filtered = prev.filter(e => e.station.id !== stationId);
            // Re-assign colors after removal
            return filtered.map((e, i) => ({ ...e, color: COMPARE_COLORS[i % COMPARE_COLORS.length] }));
        });
    };

    const clearAll = () => {
        setComparedStations([]);
    };

    const isStationActive = (id) => comparedStations.some(e => e.station.id === id);

    return (
        <div style={{ display: "flex", height: "calc(95vh - 120px)", gap: "0px", fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>
            {/* Inline tooltip styles */}
            <style>{`
                .analysis-station-tooltip {
                    background: white !important;
                    border: 1px solid #2563eb !important;
                    border-radius: 8px !important;
                    padding: 6px 12px !important;
                    font-size: 12px !important;
                    font-weight: 600 !important;
                    color: #1e3a5f !important;
                    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.15) !important;
                }
                .analysis-station-tooltip::before {
                    border-right-color: #2563eb !important;
                }
            `}</style>

            {/* Map 40% */}
            <div style={{ flex: "0 0 40%", height: "100%", position: "relative" }}>
                <MapContainer
                    center={[-8.645, 125.5077]}
                    zoom={11}
                    style={{ height: "100%", width: "100%", borderRadius: "12px", boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)", background: "#f2efe9" }}
                >
                  <MapHomeControl />
                  <MapInfoControl text="Flood analysis map showing rainfall and river conditions used for flood assessment." />
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    {stations
                        .filter((s) => s.latitude && s.longitude)
                        .map((station) => (
                            <Marker
                                key={station.id}
                                position={[station.latitude, station.longitude]}
                                icon={isStationActive(station.id) ? activeStationIcon : stationIcon}
                                eventHandlers={{ click: () => handleStationClick(station) }}
                            >
                                <Tooltip direction="right" offset={[10, 0]} opacity={1} className="analysis-station-tooltip">
                                    {station.station_name}
                                </Tooltip>
                            </Marker>
                        ))}
                    {DiliRivers && (
                        <GeoJSON data={DiliRivers} interactive={false} style={{ color: "#0099FF", weight: 2.5, fillOpacity: 0 }} />
                    )}
                </MapContainer>

                {/* Compared stations chips — bottom of map */}
                {comparedStations.length > 0 && (
                    <div
                        style={{
                            position: "absolute",
                            bottom: "12px",
                            left: "12px",
                            right: "12px",
                            zIndex: 1000,
                            background: "rgba(255, 255, 255, 0.92)",
                            backdropFilter: "blur(12px)",
                            WebkitBackdropFilter: "blur(12px)",
                            borderRadius: "10px",
                            padding: "10px 14px",
                            border: "1px solid #e2e8f0",
                            boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08)",
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span style={{ fontSize: "10px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                                Comparing {comparedStations.length} Station{comparedStations.length > 1 ? "s" : ""}
                            </span>
                            {comparedStations.length > 1 && (
                                <button
                                    onClick={clearAll}
                                    style={{
                                        background: "none", border: "none", cursor: "pointer",
                                        fontSize: "10px", color: "#94a3b8", fontWeight: 600,
                                        padding: "2px 6px", borderRadius: 4,
                                        transition: "all 0.15s",
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.color = "#e11d48"; e.currentTarget.style.background = "#fef2f2"; }}
                                    onMouseLeave={e => { e.currentTarget.style.color = "#94a3b8"; e.currentTarget.style.background = "none"; }}
                                >
                                    Clear All
                                </button>
                            )}
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                            {comparedStations.map((entry) => (
                                <div
                                    key={entry.station.id}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        background: "#f8fafc",
                                        border: "1px solid #e2e8f0",
                                        borderRadius: "6px",
                                        padding: "4px 10px",
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        color: "#1e293b",
                                        transition: "all 0.15s",
                                    }}
                                >
                                    <div style={{
                                        width: 7, height: 7, borderRadius: "50%",
                                        background: entry.color,
                                        boxShadow: `0 0 6px ${entry.color}66`,
                                        flexShrink: 0,
                                    }} />
                                    <span>{entry.station.station_name}</span>
                                    {entry.loading && (
                                        <span style={{ fontSize: "9px", color: "#94a3b8" }}>⏳</span>
                                    )}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); removeStation(entry.station.id); }}
                                        style={{
                                            background: "none", border: "none", cursor: "pointer",
                                            fontSize: "13px", color: "#94a3b8", padding: "0 0 0 2px",
                                            lineHeight: 1, display: "flex", alignItems: "center",
                                            transition: "color 0.15s",
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.color = "#e11d48"}
                                        onMouseLeave={e => e.currentTarget.style.color = "#94a3b8"}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            {comparedStations.length < 4 && (
                                <div style={{
                                    display: "flex", alignItems: "center", gap: "4px",
                                    border: "1px dashed #cbd5e1", borderRadius: "6px",
                                    padding: "4px 10px", fontSize: "10px", color: "#94a3b8", fontWeight: 500,
                                }}>
                                    + Click map to add
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Station count badge */}
                <div
                    style={{
                        position: "absolute",
                        top: "12px",
                        right: "12px",
                        zIndex: 1000,
                        background: "rgba(255, 255, 255, 0.92)",
                        backdropFilter: "blur(12px)",
                        WebkitBackdropFilter: "blur(12px)",
                        borderRadius: "8px",
                        padding: "6px 12px",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.06)",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                    }}
                >
                    <span style={{ color: "#1e293b", fontSize: "11px", fontWeight: "600" }}>
                        {stations.length} Stations
                    </span>
                </div>
            </div>

            {/* Chart 60% */}
            <div style={{ flex: "1", padding: "0 0 0 16px", display: "flex", flexDirection: "column" }}>
                {comparedStations.length > 0 ? (
                    <ForecastChart
                        comparedStations={comparedStations}
                    />
                ) : (
                    <div
                        style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f8fafc",
                            borderRadius: "12px",
                            border: "2px dashed #e2e8f0",
                            gap: "12px",
                        }}
                    >
                        <div
                            style={{
                                width: "56px",
                                height: "56px",
                                borderRadius: "50%",
                                background: "linear-gradient(135deg, #ebf2ff, #dbeafe)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "24px",
                            }}
                        >
                            📊
                        </div>
                        <div style={{ textAlign: "center" }}>
                            <p style={{ color: "#475569", fontSize: "15px", fontWeight: "600", margin: "0 0 4px 0" }}>
                                No Station Selected
                            </p>
                            <p style={{ color: "#94a3b8", fontSize: "13px", margin: 0 }}>
                                Click a station marker on the map to view discharge analysis
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
